import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@neo/primitives/dialog'
import { Button } from '@neo/index'
import type { ReactNode } from 'react'

interface DefaultFlowWarningProps {
  isDefaultFlow: boolean
  defaultForChannels: string[]
  trigger: ReactNode
  onPauseConfirm?: () => void
  isPauseDisabled?: boolean
  title?: string
  actionText?: string
}

/**
 * A reusable component for displaying a warning dialog when attempting to pause a default flow
 */
export const DefaultFlowWarning = ({
  isDefaultFlow,
  defaultForChannels,
  trigger,
  onPauseConfirm,
  isPauseDisabled = false,
  title = 'Pause the AI Agent?',
  actionText = 'Pause',
}: DefaultFlowWarningProps) => (
  <Dialog>
    <DialogTrigger asChild>{trigger}</DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{isDefaultFlow ? 'Cannot Pause Default Agent' : title}</DialogTitle>
        <DialogDescription>
          {isDefaultFlow ? (
            <>
              This agent cannot be paused because it is set as the default agent for the following
              channel(s):
              <ul className="mt-2 list-disc pl-5">
                {defaultForChannels.map((channel) => (
                  <li key={channel}>
                    {channel.charAt(0).toUpperCase() + channel.slice(1).toLowerCase()}
                  </li>
                ))}
              </ul>
              To pause this agent, first update the channel settings to use a different default
              agent.
            </>
          ) : (
            <>
              This agent will stop handling new triggers until resumed.
              <br />
              Are you sure you want to pause?
            </>
          )}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        {isDefaultFlow ? (
          <Button variant="default" asChild>
            <DialogClose>OK</DialogClose>
          </Button>
        ) : (
          <>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              onClick={onPauseConfirm}
              disabled={isPauseDisabled}
              data-testid="button-confirm-pause"
            >
              {actionText}
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
)
